import React, { useEffect, useState } from 'react'
import { Circle, Group, Rect } from 'react-konva'
import { exportTransformation } from '../util/exportTransformation' // Replace with the correct path

import { Filter, importTransformation } from '../util/importTransformation'

const AnnotationFilter: React.FC<{
    currentFilter: any
    handleContextUpdate: any
    imageSize: any
}> = (props) => {
    const [importedFilter, setImportedFilter] = useState<Filter | null>(null)
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
    const [circlePosition, setCirclePosition] = useState<any>()

    useEffect(() => {
        if (importedFilter) {
            if (importedFilter.type === 'FilterInvalidTrajectory_Double') {
                if (importedFilter?.properties._rectangle_in_out)
                    setCirclePosition({
                        in: {
                            x: 0,
                            y: 0
                        },
                        out: {
                            x: 0,
                            y: 0
                        },
                        in_out: {
                            x: importedFilter.properties._rectangle_in_out._x0,
                            y: importedFilter.properties._rectangle_in_out._y0
                        }
                    })
            } else {
                if (importedFilter?.properties._rectangle_in && importedFilter?.properties._rectangle_out)
                    setCirclePosition({
                        in: {
                            x: importedFilter?.properties._rectangle_in._x0,
                            y: importedFilter?.properties._rectangle_in._y0
                        },
                        out: {
                            x: importedFilter?.properties._rectangle_out._x0,
                            y: importedFilter?.properties._rectangle_out._y0
                        },
                        in_out: {
                            x: 0,
                            y: 0
                        }
                    })
            }
        }
    }, [importedFilter])

    useEffect(() => {
        if (props.imageSize) {
            setCanvasSize({
                width: props.imageSize.width,
                height: props.imageSize.height
            })
        }
    }, [props.imageSize])

    useEffect(() => {
        if (props.currentFilter) {
            const imported = importTransformation(props.currentFilter, 1920, 1080)
            setImportedFilter(imported)
        } else {
            setImportedFilter(null)
        }
    }, [props.currentFilter])

    const handleCircleDragMove = (event: any) => {
        const newPos = event.target.position()
        if (importedFilter) {
            const updatedFilter = { ...importedFilter }
            if (event.target.attrs.id === 'IN/OUT' && updatedFilter.properties._rectangle_in_out) {
                setCirclePosition((prevPositions: any) => ({
                    ...prevPositions,
                    in_out: { x: newPos.x, y: newPos.y }
                }))
                const deltaX = newPos.x - circlePosition.in_out.x
                const deltaY = newPos.y - circlePosition.in_out.y

                updatedFilter.properties._rectangle_in_out._x0 += deltaX
                updatedFilter.properties._rectangle_in_out._y0 += deltaY
                setImportedFilter(updatedFilter)
            } else if (event.target.attrs.id === 'IN' && updatedFilter.properties._rectangle_in) {
                setCirclePosition((prevPositions: any) => ({
                    ...prevPositions,
                    in: { x: newPos.x, y: newPos.y }
                }))
                const deltaX = newPos.x - circlePosition.in.x
                const deltaY = newPos.y - circlePosition.in.y

                updatedFilter.properties._rectangle_in._x0 += deltaX
                updatedFilter.properties._rectangle_in._y0 += deltaY
                setImportedFilter(updatedFilter)
            } else {
                if (updatedFilter.properties._rectangle_out) {
                    setCirclePosition((prevPositions: any) => ({
                        ...prevPositions,
                        out: { x: newPos.x, y: newPos.y }
                    }))
                    const deltaX = newPos.x - circlePosition.out.x
                    const deltaY = newPos.y - circlePosition.out.y

                    updatedFilter.properties._rectangle_out._x0 += deltaX
                    updatedFilter.properties._rectangle_out._y0 += deltaY
                    setImportedFilter(updatedFilter)
                }
            }
        }
    }

    const handleCircleDragEnd = () => {
        if (importedFilter) props.handleContextUpdate(exportTransformation(importedFilter, 1920, 1080))
    }

    const dragBoundFunc = (pos: any, rectWidth: any, rectHeight: any) => {
        const canvasWidth = canvasSize.width
        const canvasHeight = canvasSize.height
        const rectangleWidth = rectWidth
        const rectangleHeight = rectHeight

        const minX = 0
        const minY = 0
        const maxX = canvasWidth - rectangleWidth
        const maxY = canvasHeight - rectangleHeight

        const x = Math.max(minX, Math.min(pos.x, maxX))
        const y = Math.max(minY, Math.min(pos.y, maxY))

        return { x, y }
    }

    const handleRectDragMove = (event: any) => {
        const rectPosition = event.target.position()

        if (importedFilter) {
            const updatedFilter = { ...importedFilter }
            if (event.target.attrs.id === 'IN/OUT' && updatedFilter.properties._rectangle_in_out) {
                updatedFilter.properties._rectangle_in_out._x0 = rectPosition.x
                updatedFilter.properties._rectangle_in_out._y0 = rectPosition.y
                updatedFilter.properties._rectangle_in_out._x1 = rectPosition.x + event.target.width()
                updatedFilter.properties._rectangle_in_out._y1 = rectPosition.y + event.target.height()
            } else if (event.target.attrs.id === 'IN' && updatedFilter.properties._rectangle_in) {
                updatedFilter.properties._rectangle_in._x0 = rectPosition.x
                updatedFilter.properties._rectangle_in._y0 = rectPosition.y
                updatedFilter.properties._rectangle_in._x1 = rectPosition.x + event.target.width()
                updatedFilter.properties._rectangle_in._y1 = rectPosition.y + event.target.height()
            } else {
                if (updatedFilter.properties._rectangle_out) {
                    updatedFilter.properties._rectangle_out._x0 = rectPosition.x
                    updatedFilter.properties._rectangle_out._y0 = rectPosition.y
                    updatedFilter.properties._rectangle_out._x1 = rectPosition.x + event.target.width()
                    updatedFilter.properties._rectangle_out._y1 = rectPosition.y + event.target.height()
                }
            }
            setImportedFilter(updatedFilter)
        }
    }

    const handleRectDragEnd = () => {
        if (importedFilter) props.handleContextUpdate(exportTransformation(importedFilter, 1920, 1080))
    }

    if (!importedFilter || !circlePosition || !canvasSize.width || !canvasSize.height) {
        return null
    }

    const filter = importedFilter
    return (
        <Group name="polygon" preventDefault={false}>
            {filter.type === 'FilterInvalidTrajectory_Double' && filter.properties._rectangle_in_out && (
                <React.Fragment>
                    <Rect
                        x={filter.properties._rectangle_in_out._x0}
                        y={filter.properties._rectangle_in_out._y0}
                        width={filter.properties._rectangle_in_out._x1 - filter.properties._rectangle_in_out._x0}
                        height={filter.properties._rectangle_in_out._y1 - filter.properties._rectangle_in_out._y0}
                        stroke="rgba(255, 165, 0, 1)"
                        strokeWidth={4}
                        fill="rgba(128, 128, 128, 0.5)"
                        draggable
                        onDragEnd={handleRectDragEnd}
                        onDragMove={handleRectDragMove}
                        id={'IN/OUT'}
                        dragBoundFunc={(pos: any) =>
                            dragBoundFunc(
                                pos,
                                (filter?.properties?._rectangle_in_out?._x1 || 0) -
                                    (filter?.properties?._rectangle_in_out?._x0 || 0),
                                (filter?.properties?._rectangle_in_out?._y1 || 0) -
                                    (filter?.properties?._rectangle_in_out?._y0 || 0)
                            )
                        }
                    />
                    <Circle
                        x={circlePosition.in_out.x}
                        y={circlePosition.in_out.y}
                        radius={8}
                        fill="#e95d95"
                        stroke="#515151"
                        strokeWidth={2}
                        draggable
                        onDragMove={handleCircleDragMove}
                        onDragEnd={handleCircleDragEnd}
                        id={'IN/OUT'}
                        dragBoundFunc={(pos: any) => {
                            const rect = importedFilter.properties._rectangle_in_out
                            if (rect) {
                                const maxX = rect._x1
                                const maxY = rect._y1

                                const x = Math.min(Math.max(pos.x, 0), maxX) // Adjusted for circle radius
                                const y = Math.min(Math.max(pos.y, 0), maxY) // Adjusted for circle radius

                                return { x, y }
                            }
                            return pos
                        }}
                    />
                </React.Fragment>
            )}

            {(filter.type === 'FilterInvalidTrajectory_Soft' ||
                filter.type === 'ClassifyDirection_IN_Soft' ||
                filter.type === 'ClassifyDirection_OUT_Soft') &&
                filter.properties._rectangle_in && (
                    <React.Fragment>
                        <Rect
                            x={filter.properties._rectangle_in._x0}
                            y={filter.properties._rectangle_in._y0}
                            width={filter.properties._rectangle_in._x1 - filter.properties._rectangle_in._x0}
                            height={filter.properties._rectangle_in._y1 - filter.properties._rectangle_in._y0}
                            stroke="rgba(0, 255, 0, 1)"
                            strokeWidth={4}
                            fill="rgba(128, 128, 128, 0.5)"
                            draggable
                            onDragEnd={handleRectDragEnd}
                            onDragMove={handleRectDragMove}
                            id={'IN'}
                            dragBoundFunc={(pos: any) =>
                                dragBoundFunc(
                                    pos,
                                    (filter?.properties?._rectangle_in?._x1 || 0) -
                                        (filter?.properties?._rectangle_in?._x0 || 0),
                                    (filter?.properties?._rectangle_in?._y1 || 0) -
                                        (filter?.properties?._rectangle_in?._y0 || 0)
                                )
                            }
                        />
                        <Circle
                            x={circlePosition.in.x}
                            y={circlePosition.in.y}
                            radius={8}
                            fill="#e95d95"
                            stroke="#515151"
                            strokeWidth={2}
                            draggable
                            onDragMove={handleCircleDragMove}
                            onDragEnd={handleCircleDragEnd}
                            id={'IN'}
                            dragBoundFunc={(pos: any) => {
                                const rect = importedFilter.properties._rectangle_in
                                if (rect) {
                                    const maxX = rect._x1
                                    const maxY = rect._y1

                                    const x = Math.min(Math.max(pos.x, 0), maxX) // Adjusted for circle radius
                                    const y = Math.min(Math.max(pos.y, 0), maxY) // Adjusted for circle radius

                                    return { x, y }
                                }
                                return pos
                            }}
                        />
                    </React.Fragment>
                )}

            {(filter.type === 'FilterInvalidTrajectory_Soft' ||
                filter.type === 'ClassifyDirection_IN_Soft' ||
                filter.type === 'ClassifyDirection_OUT_Soft') &&
                filter.properties._rectangle_out && (
                    <React.Fragment>
                        <Rect
                            x={filter.properties._rectangle_out._x0}
                            y={filter.properties._rectangle_out._y0}
                            width={filter.properties._rectangle_out._x1 - filter.properties._rectangle_out._x0}
                            height={filter.properties._rectangle_out._y1 - filter.properties._rectangle_out._y0}
                            stroke="rgba(255, 0, 0, 1)"
                            strokeWidth={4}
                            fill="rgba(128, 128, 128, 0.5)"
                            draggable
                            onDragEnd={handleRectDragEnd}
                            onDragMove={handleRectDragMove}
                            id={'OUT'}
                            dragBoundFunc={(pos: any) =>
                                dragBoundFunc(
                                    pos,
                                    (filter?.properties?._rectangle_out?._x1 || 0) -
                                        (filter?.properties?._rectangle_out?._x0 || 0),
                                    (filter?.properties?._rectangle_out?._y1 || 0) -
                                        (filter?.properties?._rectangle_out?._y0 || 0)
                                )
                            }
                        />
                        <Circle
                            x={circlePosition.out.x}
                            y={circlePosition.out.y}
                            radius={8}
                            fill="blue"
                            draggable
                            onDragMove={handleCircleDragMove}
                            onDragEnd={handleCircleDragEnd}
                            id={'OUT'}
                            dragBoundFunc={(pos: any) => {
                                const rect = importedFilter.properties._rectangle_out
                                if (rect) {
                                    const maxX = rect._x1
                                    const maxY = rect._y1

                                    const x = Math.min(Math.max(pos.x, 0), maxX) // Adjusted for circle radius
                                    const y = Math.min(Math.max(pos.y, 0), maxY) // Adjusted for circle radius

                                    return { x, y }
                                }
                                return pos
                            }}
                        />
                    </React.Fragment>
                )}
        </Group>
    )
}

export default AnnotationFilter
